import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '@/utils/api'

export const useDataStore = defineStore('data', () => {
  // Состояние
  const collections = ref([])
  const isLoading = ref(false)
  const savedFilters = ref([])

  // API методы
  const getCollections = async () => {
    try {
      console.log('📋 Загрузка списка коллекций...')

      const response = await apiClient.get('/api/collections')
      collections.value = response.data.collections

      console.log('✅ Коллекции загружены:', collections.value.length)
      return collections.value
    } catch (error) {
      console.error('❌ Ошибка загрузки коллекций:', error)
      throw error
    }
  }

  const filterData = async (filterRequest) => {
    try {
      console.log('🔍 Фильтрация данных:', filterRequest.collection_name)

      const response = await apiClient.post('/api/data/filter', filterRequest)
      const data = response.data

      console.log(`✅ Получено ${data.count} записей из ${data.total}`)
      return data
    } catch (error) {
      console.error('❌ Ошибка фильтрации данных:', error)
      throw error
    }
  }

  const aggregateData = async (aggregationRequest) => {
    try {
      console.log('📊 Агрегация данных:', aggregationRequest.collection_name)

      const response = await apiClient.post('/api/data/aggregate', aggregationRequest)
      const data = response.data

      console.log(`✅ Агрегация выполнена: ${data.total_groups} групп`)
      return data
    } catch (error) {
      console.error('❌ Ошибка агрегации данных:', error)
      throw error
    }
  }

  const getQuickStats = async () => {
    try {
      console.log('⚡ Загрузка быстрой статистики...')

      const response = await apiClient.get('/api/data/quick-stats')
      const data = response.data

      console.log('✅ Быстрая статистика получена')
      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки быстрой статистики:', error)
      throw error
    }
  }

  const getCollectionStats = async (collectionName) => {
    try {
      console.log(`📊 Загрузка статистики для ${collectionName}...`)

      const response = await apiClient.get(`/api/data/stats/${collectionName}`)
      const data = response.data

      console.log('✅ Статистика коллекции получена')
      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки статистики коллекции:', error)
      throw error
    }
  }

  const getCollectionSchema = async (collectionName) => {
    try {
      console.log(`🔧 Загрузка схемы для ${collectionName}...`)

      const response = await apiClient.get(`/api/data/schema/${collectionName}`)
      const data = response.data

      console.log('✅ Схема коллекции получена')
      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки схемы коллекции:', error)
      throw error
    }
  }

  // Получение элемента по ID из указанной коллекции
  const getItemById = async (collectionName, itemId) => {
    try {
      console.log(`🔍 Загрузка элемента ${itemId} из коллекции ${collectionName}...`)

      const response = await apiClient.get(`/api/data/${collectionName}/${itemId}`)
      const data = response.data

      console.log('✅ Элемент получен:', data.TITLE || data.NAME || itemId)
      return data
    } catch (error) {
      console.error(`❌ Ошибка загрузки элемента ${itemId}:`, error)
      
      // Если элемент не найден, возвращаем более понятную ошибку
      if (error.response?.status === 404) {
        throw new Error(`Элемент с ID ${itemId} не найден`)
      }
      
      throw error
    }
  }

  // Обновление элемента в коллекции
  const updateItem = async (collectionName, itemId, updateData) => {
    try {
      console.log(`✏️ Обновление элемента ${itemId} в коллекции ${collectionName}...`)

      const response = await apiClient.put(`/api/data/${collectionName}/${itemId}`, updateData)
      const data = response.data

      console.log('✅ Элемент обновлен')
      return data
    } catch (error) {
      console.error(`❌ Ошибка обновления элемента ${itemId}:`, error)
      throw error
    }
  }

  // Создание нового элемента в коллекции
  const createItem = async (collectionName, itemData) => {
    try {
      console.log(`➕ Создание элемента в коллекции ${collectionName}...`)

      const response = await apiClient.post(`/api/data/${collectionName}`, itemData)
      const data = response.data

      console.log('✅ Элемент создан:', data.ID || data._id)
      return data
    } catch (error) {
      console.error(`❌ Ошибка создания элемента:`, error)
      throw error
    }
  }

  // Удаление элемента из коллекции
  const deleteItem = async (collectionName, itemId) => {
    try {
      console.log(`🗑️ Удаление элемента ${itemId} из коллекции ${collectionName}...`)

      await apiClient.delete(`/api/data/${collectionName}/${itemId}`)

      console.log('✅ Элемент удален')
      return true
    } catch (error) {
      console.error(`❌ Ошибка удаления элемента ${itemId}:`, error)
      throw error
    }
  }

  // Получение связанных элементов
  const getRelatedItems = async (collectionName, itemId, relatedCollection, relationField = null) => {
    try {
      console.log(`🔗 Загрузка связанных ${relatedCollection} для ${collectionName}:${itemId}...`)

      let url = `/api/data/${collectionName}/${itemId}/related/${relatedCollection}`
      if (relationField) {
        url += `?field=${relationField}`
      }

      const response = await apiClient.get(url)
      const data = response.data

      console.log(`✅ Найдено ${data.length} связанных элементов`)
      return data
    } catch (error) {
      console.error(`❌ Ошибка загрузки связанных данных:`, error)
      // Возвращаем пустой массив вместо ошибки для связанных данных
      return []
    }
  }

  // Выполнение действия над элементом (конвертация, изменение статуса и т.д.)
  const performItemAction = async (collectionName, itemId, action, actionData = {}) => {
    try {
      console.log(`⚡ Выполнение действия "${action}" для ${collectionName}:${itemId}...`)

      const response = await apiClient.post(`/api/data/${collectionName}/${itemId}/actions/${action}`, actionData)
      const data = response.data

      console.log(`✅ Действие "${action}" выполнено`)
      return data
    } catch (error) {
      console.error(`❌ Ошибка выполнения действия "${action}":`, error)
      throw error
    }
  }

  // Сохраненные фильтры
  const getSavedFilters = async (collectionName = null) => {
    try {
      console.log('💾 Загрузка сохраненных фильтров...')

      let url = '/api/data/filters'
      if (collectionName) {
        url += `?collection_name=${collectionName}`
      }

      const response = await apiClient.get(url)
      savedFilters.value = response.data

      console.log(`✅ Загружено ${savedFilters.value.length} фильтров`)
      return savedFilters.value
    } catch (error) {
      console.error('❌ Ошибка загрузки сохраненных фильтров:', error)
      throw error
    }
  }

  const saveFilter = async (filterData) => {
    try {
      console.log('💾 Сохранение фильтра:', filterData.name)

      const response = await apiClient.post('/api/data/filters', filterData)
      const savedFilter = response.data

      // Добавляем в локальный список
      savedFilters.value.unshift(savedFilter)

      console.log('✅ Фильтр сохранен')
      return savedFilter
    } catch (error) {
      console.error('❌ Ошибка сохранения фильтра:', error)
      throw error
    }
  }

  const deleteFilter = async (filterId) => {
    try {
      console.log('🗑️ Удаление фильтра:', filterId)

      await apiClient.delete(`/api/data/filters/${filterId}`)

      // Удаляем из локального списка
      const index = savedFilters.value.findIndex(f => f.id === filterId)
      if (index > -1) {
        savedFilters.value.splice(index, 1)
      }

      console.log('✅ Фильтр удален')
    } catch (error) {
      console.error('❌ Ошибка удаления фильтра:', error)
      throw error
    }
  }

  // Экспорт данных
  const exportData = async (exportRequest) => {
    try {
      console.log('📤 Экспорт данных:', exportRequest.format)

      const response = await apiClient.post('/api/data/export', exportRequest, {
        responseType: 'blob'
      })

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      // Получаем имя файла из заголовков
      const contentDisposition = response.headers['content-disposition']
      let filename = `export_${Date.now()}.${exportRequest.format}`

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/)
        if (match) {
          filename = match[1]
        }
      }

      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      console.log('✅ Файл скачан:', filename)
    } catch (error) {
      console.error('❌ Ошибка экспорта данных:', error)
      throw error
    }
  }

  // ВАЖНО: Возвращаем ВСЕ методы
  return {
    // Состояние
    collections,
    isLoading,
    savedFilters,

    // Основные методы данных
    getCollections,
    filterData,
    aggregateData,
    getQuickStats,
    getCollectionStats,
    getCollectionSchema,

    // CRUD операции
    getItemById,
    updateItem,
    createItem,
    deleteItem,
    getRelatedItems,
    performItemAction,

    // Фильтры
    getSavedFilters,
    saveFilter,
    deleteFilter,

    // Экспорт
    exportData
  }
})